import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { t } from "../../lib/i18n";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Minimize2, Minus, Pause, Play, Plus, X } from "lucide-react";
import { geoTransverseMercator, geoPath } from "d3-geo";
import { getCached } from "../../lib/api";
import { FloorLabel, CountUp, useInView, reduced } from "../motion/Reveal";
import "./map.css";

/* Where EQVA works. A calm, full-bleed map of Norway – real coastline, soft light, no instrument chrome – with one
   reading column: a short headline and the numbers up top, an image-led project card at the bottom. The land is
   painted on a canvas in screen space for the view it was drawn for; between views the canvas glides on a composited
   CSS transform and is repainted crisp when it settles. The SVG above only carries what moves: pins and labels.
   Mobile: headline, legend, map, then a swipeable rail of cards that drives the map. */

export const TYPES = [
  { id: "industri", label: t("Industri og smelteverk"), color: "#8fbce9", img: "/images/marked-industri-640.webp" },
  { id: "havbruk", label: t("Hav og havbruk"), color: "#6cc8b6", img: "/images/marked-havbruk-640.webp" },
  { id: "energi", label: t("Energi og kraft"), color: "#e8c06a", img: "/images/marked-energi-640.webp" },
  { id: "nye-industrier", label: t("Forsvar og nye industrier"), color: "#e4ad86", img: "/images/aktuelt-datasenter-640.webp" },
];
const TYPE = Object.fromEntries(TYPES.map((t) => [t.id, t]));
const COMPANY_IMG = new Set(["bks-industri", "bks-vvs", "bks-power-automation", "marine-support", "zenit-engineering", "kvinnherad-elektro", "imtas", "austevoll-rorteknikk", "einar-ogrey-farsund", "fossberg-kraft"]);
const imgFor = (p) => { const c = p.companies?.find((s) => COMPANY_IMG.has(s)); return c ? `/images/selskap-${c}-640.webp` : TYPE[p.type]?.img; };
const CITIES = [
  { n: "Oslo", c: [10.752, 59.913], dx: 8, dy: 3 }, { n: "Bergen", c: [5.324, 60.392], dx: -9, dy: -7, anchor: "end" },
  { n: "Trondheim", c: [10.395, 63.43], dx: 8, dy: 3 }, { n: "Bodø", c: [14.405, 67.28], dx: 8, dy: 3 },
  { n: "Tromsø", c: [18.956, 69.649], dx: 10, dy: -6 }, { n: "Stavanger", c: [5.733, 58.97], dx: -9, dy: 3, anchor: "end" },
  { n: "Haugesund", c: [5.268, 59.413], dx: -9, dy: 3, anchor: "end", min: 2.4 }, { n: "Odda", c: [6.545, 60.069], dx: 8, dy: 3, min: 2.4 },
  { n: "Ålesund", c: [6.149, 62.472], dx: -9, dy: 3, anchor: "end", min: 2.4 }, { n: "Kristiansand", c: [7.995, 58.146], dx: 8, dy: 3, min: 2.4 },
  { n: "Narvik", c: [17.427, 68.438], dx: 8, dy: 3, min: 2.4 }, { n: "Mo i Rana", c: [14.142, 66.313], dx: 8, dy: 3, min: 2.4 },
  { n: "Molde", c: [7.158, 62.737], dx: 8, dy: -6, min: 2.4 }, { n: "Egersund", c: [6.0, 58.451], dx: 8, dy: 3, min: 2.4 },
];
/* Where each company is based (lng, lat) – shown as a hollow square when a company is picked or hovered */
const BASES = {
  "bks-industri": [{ place: "Sunde", c: [5.717, 59.83] }],
  "bks-vvs": [{ place: "Straume", c: [5.13, 60.36] }],
  "bks-power-automation": [{ place: "Sunde", c: [5.717, 59.83] }],
  "marine-support": [{ place: "Storebø", c: [5.23, 60.09] }],
  "zenit-engineering": [{ place: "Sunde", c: [5.717, 59.83] }, { place: "Stord", c: [5.5, 59.78] }],
  "kvinnherad-elektro": [{ place: "Husnes", c: [5.76, 59.86] }],
  imtas: [{ place: "Mo i Rana", c: [14.14, 66.31] }, { place: "Harstad", c: [16.54, 68.8] }],
  "austevoll-rorteknikk": [{ place: "Austevoll", c: [5.23, 60.09] }],
  "einar-ogrey-farsund": [{ place: "Farsund", c: [6.8, 58.09] }],
  "fossberg-kraft": [{ place: "Husnes", c: [5.76, 59.86] }],
};
const K_MAX = 9, K_PIN = 4.5, SPOT_MS = 6000, IDLE_MS = 14000;
/* Marker geometry (screen px). A single project is a bead: white ring (BEAD_R) around a coloured core.
   A cluster is a pearl-white disc whose radius grows gently with the count, ringed by its mix of market colours. */
const BEAD_R = 8, MARK_GAP = 6;
const clusterR = (n) => 13 + 2.2 * Math.sqrt(n);
const markR = (g) => (g.items.length > 1 ? clusterR(g.items.length) : BEAD_R);

const fetchGeo = () => fetch("/geo/norway.json").then((r) => r.json());
/* Merge markers until none would touch on screen at zoom k – so beads and discs never overlap, whatever the density */
const clusterize = (pins, k) => {
  const groups = pins.map((p) => ({ items: [p], x: p.x, y: p.y }));
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        const a = groups[i], b = groups[j];
        if (Math.hypot(a.x - b.x, a.y - b.y) * k < markR(a) + markR(b) + MARK_GAP) {
          const items = [...a.items, ...b.items];
          groups.splice(j, 1); groups.splice(i, 1);
          groups.push({ items, x: items.reduce((s, p) => s + p.x, 0) / items.length, y: items.reduce((s, p) => s + p.y, 0) / items.length });
          merged = true; break outer;
        }
      }
    }
  }
  return groups.map((g) => ({ ...g, key: g.items.map((p) => p.slug).sort().join("|") }));
};
/* Thin ring around a cluster disc, one arc per market type, proportional to its share */
const ringArcs = (items, r) => {
  const counts = TYPES.map((t) => [t, items.filter((p) => p.type === t.id).length]).filter(([, n]) => n > 0);
  if (counts.length === 1) return [{ id: counts[0][0].id, color: counts[0][0].color, full: true }];
  const total = items.length, gap = 3.5 / r, span = Math.PI * 2 - gap * counts.length;
  let a = -Math.PI / 2 + gap / 2;
  return counts.map(([t, n]) => {
    const a0 = a, a1 = a + (n / total) * span; a = a1 + gap;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return { id: t.id, color: t.color, d: `M${(r * Math.cos(a0)).toFixed(2)} ${(r * Math.sin(a0)).toFixed(2)}A${r} ${r} 0 ${large} 1 ${(r * Math.cos(a1)).toFixed(2)} ${(r * Math.sin(a1)).toFixed(2)}` };
  });
};
const regionLabel = (items) => { const pl = [...new Set(items.map((p) => p.place))]; return pl.length <= 3 ? pl.join(" · ") : `${pl.slice(0, 3).join(" · ")} +${pl.length - 3}`; };
const companiesOf = (p) => p.company_items?.map((c) => c.name).join(" · ") || "";

const Dot = ({ type, on = true, className = "" }) => (
  <span className={`map-dot ${on ? "" : "is-off"} ${className}`} style={{ "--c": TYPE[type]?.color }} aria-hidden="true" />
);

const Logos = ({ p, byCompany, testid, h = "h-5" }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
    {p.company_items?.map((c) => { const co = byCompany?.[c.slug]; return (
      <Link key={c.slug} to={`/selskaper/${c.slug}`} data-testid={`${testid}-company-${c.slug}`} aria-label={t("{name} – se selskapet", { name: c.name })} title={c.name} className="map-logo-link">
        {co?.logo_white ? <img src={co.logo_white} alt="" className={`${h} w-auto max-w-[160px]`} loading="lazy" decoding="async" /> : <span className="t-small text-white">{c.name}</span>}
      </Link>); })}
  </div>
);

const ProjectCard = ({ p, idx, total, byCompany, onPrev, onNext, spot, spotOn, onToggleSpot, compact = false, testid = "map-card" }) => (
  <article data-testid={testid} data-spot={spot ? "true" : "false"} className={`map-card relative ${compact ? "" : "fade-in"}`} style={compact ? undefined : { animationDuration: "320ms" }}>
    <div className="map-card-media">
      <img src={imgFor(p)} alt="" loading={compact ? "lazy" : "eager"} decoding="async" width="640" height="400" />
      <span className="map-card-idx tabular" data-testid={`${testid}-index`}>{idx + 1} / {total}</span>
    </div>
    <div className="map-card-body pt-4">
      <Logos p={p} byCompany={byCompany} testid={testid} />
      <h3 className="mt-3 text-[clamp(1.125rem,1.3vw,1.375rem)] font-medium leading-snug tracking-[-0.02em] text-white">{p.name}</h3>
      <p className="mt-1.5 t-small text-white/55 tabular">{p.place} · {p.period}</p>
      <div className="mt-4 flex items-end justify-between gap-4">
        <p className="flex min-w-0 items-center gap-2 t-label text-[11px] text-white/50"><Dot type={p.type} /><span className="truncate">{TYPE[p.type]?.label}</span></p>
        {(onPrev || onNext || onToggleSpot) && (
          <div className="-mb-1.5 -mr-2 flex shrink-0 items-center">
            {onToggleSpot && <button type="button" data-testid="map-spot-toggle" aria-pressed={spotOn} aria-label={spotOn ? t("Pause visningen") : t("Start visningen")} title={spotOn ? t("Pause visningen") : t("Start visningen")} onClick={onToggleSpot} className="map-icon-btn">{spotOn ? <Pause size={12} /> : <Play size={12} />}</button>}
            {onPrev && <button type="button" data-testid="map-prev" aria-label={t("Forrige oppdrag")} onClick={onPrev} className="map-icon-btn"><ChevronLeft size={16} /></button>}
            {onNext && <button type="button" data-testid="map-next" aria-label={t("Neste oppdrag")} onClick={onNext} className="map-icon-btn"><ChevronRight size={16} /></button>}
          </div>
        )}
      </div>
      {p.source && <p className="map-card-source mt-2 text-[11.5px] text-white/30">{p.source}</p>}
    </div>
    {spot && <span className="map-card-progress" style={{ animationDuration: `${SPOT_MS}ms` }} aria-hidden="true" />}
  </article>
);

export const MapFloor = () => {
  const [geo, setGeo] = useState(null);
  const [projects, setProjects] = useState(null);
  const [companiesData, setCompaniesData] = useState(null);
  const [company, setCompany] = useState(null);
  const [hoverCompany, setHoverCompany] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState(() => new Set(TYPES.map((t) => t.id)));
  const [view, setView] = useState(null);
  const [hover, setHover] = useState(null);
  const [active, setActive] = useState(null);
  const [openCluster, setOpenCluster] = useState(null);
  const [hoverCluster, setHoverCluster] = useState(null);
  const [rowHover, setRowHover] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [spot, setSpot] = useState(null);
  const [spotOn, setSpotOn] = useState(() => !reduced());
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1280);
  const [dragging, setDragging] = useState(false);
  const [drawn, setDrawn] = useState({ k: 1, tx: 0, ty: 0 });
  const [snapping, setSnapping] = useState(false);
  const [ref, seen] = useInView({ threshold: 0.15 });
  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const canvasRef = useRef(null);
  const offRef = useRef(null);
  const railRef = useRef(null);
  const lastTouch = useRef(0);
  const drag = useRef(null);
  const dragged = useRef(false);
  const railTimer = useRef(null);

  useEffect(() => {
    Promise.all([fetchGeo(), getCached("/projects", 10 * 60_000), getCached("/companies", 10 * 60_000).catch(() => [])])
      .then(([g, p, c]) => { setGeo(g); setProjects(p); setCompaniesData(c); }).catch(() => setErr(true));
  }, []);
  useLayoutEffect(() => {
    const el = stageRef.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => { setSize({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height) }); setDesktop(window.innerWidth >= 1280); });
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const W = size.w, H = size.h;
  const mapLeft = 0;
  const scene = useMemo(() => {
    if (!geo || !projects || !W || !H) return null;
    const norwayF = geo.features.find((f) => f.properties.id === "norway");
    /* Upright Norway, as big as the stage allows, centred between the text column and the card column */
    const projection = desktop
      ? geoTransverseMercator().rotate([-15, 0]).fitExtent([[W * 0.26, 28], [W * 0.66, H - 28]], norwayF)
      : geoTransverseMercator().rotate([-15, 0]).fitExtent([[78, 36], [W - 16, H - 36]], norwayF);
    const pathGen = geoPath(projection);
    const pins = projects.map((p) => { const [x, y] = projection([p.lng, p.lat]); return { ...p, x, y }; });
    const lats = pins.map((p) => p.lat), lo = Math.min(...lats), hi = Math.max(...lats);
    for (const p of pins) p.t = (p.lat - lo) / Math.max(1e-6, hi - lo);
    const bases = Object.fromEntries(Object.entries(BASES).map(([slug, list]) => [slug, list.map((b) => { const [x, y] = projection(b.c); return { ...b, x, y }; })]));
    return { norway: pathGen(norwayF), context: geo.features.filter((f) => f.properties.ctx).map((f) => pathGen(f)), pins, bases, cities: CITIES.map((c) => { const [x, y] = projection(c.c); return { ...c, x, y }; }) };
  }, [geo, projects, W, H, desktop]);

  const pins = scene?.pins;
  const byCompany = useMemo(() => Object.fromEntries((companiesData || []).map((c) => [c.slug, c])), [companiesData]);
  const brands = useMemo(() => [...(companiesData || [])].filter((c) => c.logo_white).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [companiesData]);
  const visible = useMemo(() => (pins || []).filter((p) => filter.has(p.type) && (!company || p.companies?.includes(company))), [pins, filter, company]);
  const listed = useMemo(() => [...visible].sort((a, b) => a.lat - b.lat), [visible]);
  const k = view?.k ?? 1, tx = view?.tx ?? 0, ty = view?.ty ?? 0;
  const groups = useMemo(() => clusterize(visible, k), [visible, k]);
  const counties = useMemo(() => new Set((pins || []).map((p) => p.county)).size, [pins]);
  const companies = useMemo(() => new Set((pins || []).flatMap((p) => p.companies)).size, [pins]);
  const groupOf = useMemo(() => { const m = {}; for (const g of groups) for (const p of g.items) m[p.slug] = g; return m; }, [groups]);
  const pinPlaces = useMemo(() => new Set(visible.map((p) => p.place)), [visible]);
  const byCounty = useMemo(() => { const m = new Map(); for (const p of listed) { if (!m.has(p.county)) m.set(p.county, []); m.get(p.county).push(p); } return [...m.entries()]; }, [listed]);

  const onScreen = (p) => { const sx = k * p.x + tx, sy = k * p.y + ty; return sx > mapLeft + 20 && sx < W - 20 && sy > 20 && sy < H - 20; };
  const spotPool = useMemo(() => (view ? listed.filter(onScreen) : listed), [listed, view, k, tx, ty, W, H, desktop]); // eslint-disable-line react-hooks/exhaustive-deps

  const clampView = (v) => {
    const kk = Math.min(K_MAX, Math.max(1, v.k));
    if (kk <= 1.001) return null;
    return { ...v, k: kk, tx: Math.min(W * 0.55, Math.max(W * 0.45 - kk * W, v.tx)), ty: Math.min(H * 0.55, Math.max(H * 0.45 - kk * H, v.ty)) };
  };
  const fitView = (items, kOverride) => {
    const xs = items.map((p) => p.x), ys = items.map((p) => p.y);
    const bx0 = Math.min(...xs), bx1 = Math.max(...xs), by0 = Math.min(...ys), by1 = Math.max(...ys);
    const mapW = W - mapLeft, padX = desktop ? mapW * 0.3 : 56, padY = desktop ? H * 0.12 : 110;
    const kk = kOverride ?? (items.length === 1 ? K_PIN : Math.min(K_MAX, Math.max(2.2, Math.min((mapW - padX * 2) / Math.max(1, bx1 - bx0), (H - padY * 2) / Math.max(1, by1 - by0)))));
    const cx = mapLeft + mapW / 2, cy = H / 2;
    return { k: kk, tx: cx - kk * (bx0 + bx1) / 2, ty: cy - kk * (by0 + by1) / 2 };
  };
  const zoomAt = (f, px, py) => setView((v) => { const k0 = v?.k ?? 1, tx0 = v?.tx ?? 0, ty0 = v?.ty ?? 0; const kk = Math.min(K_MAX, Math.max(1, k0 * f)); return clampView({ k: kk, tx: px - (px - tx0) * (kk / k0), ty: py - (py - ty0) * (kk / k0) }); });
  const touch = () => { lastTouch.current = Date.now(); setSpot(null); };
  const toggle = (id) => { touch(); setActive(null); setFilter((f) => { const n = new Set(f); if (n.has(id)) { if (n.size > 1) n.delete(id); } else n.add(id); return n; }); };
  const tight = (g) => clusterize(g.items, K_MAX).length === 1; // still one marker at full zoom → open the place list instead
  const zoomTo = (g) => { touch(); setActive(null); setHover(null); setHoverCluster(null); if (tight(g)) { setOpenCluster(g.key); setView(fitView(g.items, K_PIN)); return; } setOpenCluster(null); setView(fitView(g.items)); };
  const reset = () => { touch(); setView(null); setActive(null); setHover(null); setOpenCluster(null); };
  /* Pick a company: the map keeps only its work (plus its home base) and glides to fit it */
  const selectCompany = (slug) => {
    touch(); setActive(null); setHover(null); setOpenCluster(null); setListOpen(false);
    if (!slug || slug === company) { setCompany(null); setView(null); return; }
    setCompany(slug);
    const items = (pins || []).filter((p) => filter.has(p.type) && p.companies?.includes(slug));
    const pts = [...items, ...(scene?.bases[slug] || [])];
    if (pts.length) setView(pts.length === 1 ? fitView(pts) : fitView(pts));
  };
  const scrollRailTo = (slug) => { const rail = railRef.current, el = rail?.querySelector(`[data-slug="${slug}"]`); if (rail && el) rail.scrollTo({ left: el.offsetLeft - (rail.clientWidth - el.clientWidth) / 2, behavior: reduced() ? "auto" : "smooth" }); };
  const select = (p, { fromMap = false, move = true } = {}) => {
    touch(); setOpenCluster(null); setListOpen(false); setHover(null); setActive(p.slug);
    const g = groupOf[p.slug], clustered = g && g.items.length > 1;
    if (move && (clustered || !onScreen(p))) setView(clustered && !tight(g) ? fitView(g.items) : fitView([p]));
    if (!desktop && fromMap) scrollRailTo(p.slug);
  };
  const pick = (p) => { if (dragged.current) return; if (active === p.slug) { touch(); setActive(null); return; } select(p, { fromMap: true, move: false }); };

  /* drag to pan (only when zoomed) */
  const onPointerDown = (e) => { lastTouch.current = Date.now(); if (k <= 1 || e.button !== 0) return; drag.current = { x: e.clientX, y: e.clientY, tx, ty, moved: false, id: e.pointerId }; };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moved) { if (Math.hypot(dx, dy) < 4) return; d.moved = true; dragged.current = true; setDragging(true); e.currentTarget.setPointerCapture?.(d.id); }
    setView((v) => (v ? clampView({ ...v, tx: d.tx + dx, ty: d.ty + dy }) : v));
  };
  const onPointerUp = () => { const d = drag.current; drag.current = null; setDragging(false); if (d?.moved) setTimeout(() => { dragged.current = false; }, 60); };

  /* land painter (screen space for a given view) */
  const paintLand = (vk, vtx, vty) => {
    const cv = canvasRef.current; if (!cv || !scene) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const pw = Math.round(W * dpr), ph = Math.round(H * dpr);
    if (cv.width !== pw || cv.height !== ph) { cv.width = pw; cv.height = ph; }
    const ctx = cv.getContext("2d"); if (!ctx) return;
    const m = new DOMMatrix([vk, 0, 0, vk, vtx, vty]);
    const land = new Path2D(); land.addPath(new Path2D(scene.norway), m);
    const ctxs = scene.context.map((d) => { const q = new Path2D(); q.addPath(new Path2D(d), m); return q; });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, W, H);
    const q = 0.5, off = offRef.current || (offRef.current = document.createElement("canvas"));
    off.width = Math.max(1, Math.round(W * q)); off.height = Math.max(1, Math.round(H * q));
    const o = off.getContext("2d");
    if (o) {
      const landQ = new Path2D(); landQ.addPath(new Path2D(scene.norway), new DOMMatrix([vk * q, 0, 0, vk * q, vtx * q, vty * q]));
      o.setTransform(1, 0, 0, 1, 0, 0); o.clearRect(0, 0, off.width, off.height);
      const soft = (fill, blur, dx = 0, dy = 0) => {
        o.save(); o.translate(dx * q, dy * q);
        if ("filter" in o) { o.filter = `blur(${blur * q}px)`; o.fillStyle = fill; o.fill(landQ); }
        else { o.strokeStyle = fill; o.lineJoin = "round"; o.globalAlpha = 0.1; for (let w = blur * 2 * q; w > 0; w -= (blur * q) / 3) { o.lineWidth = w; o.stroke(landQ); } o.globalAlpha = 1; o.fillStyle = fill; o.fill(landQ); }
        o.restore();
      };
      soft("rgba(2, 10, 20, 0.55)", 26, 10, 18);     /* the plate casts a soft shadow */
      soft("rgba(62, 124, 177, 0.2)", 30);           /* faint cool light around the coast */
      soft("rgba(156, 199, 238, 0.14)", 8);
      ctx.drawImage(off, 0, 0, W, H);
    }
    ctx.fillStyle = "rgba(255,255,255,0.03)"; ctx.strokeStyle = "rgba(255,255,255,0.07)"; ctx.lineWidth = 0.5; ctx.lineJoin = "round";
    for (const c of ctxs) { ctx.fill(c); ctx.stroke(c); }
    const g = ctx.createLinearGradient(mapLeft, H, W, 0); g.addColorStop(0, "#1a3d62"); g.addColorStop(0.5, "#12314f"); g.addColorStop(1, "#0f2a46");
    ctx.fillStyle = g; ctx.fill(land);
    ctx.save(); ctx.clip(land); ctx.strokeStyle = "rgba(190,219,245,0.16)"; ctx.lineWidth = 2.2; ctx.stroke(land); ctx.restore();
    ctx.strokeStyle = "rgba(214,232,250,0.55)"; ctx.lineWidth = 0.8; ctx.stroke(land);
  };
  useEffect(() => {
    if (!scene || dragging) return undefined;
    const settled = drawn.k === k && drawn.tx === tx && drawn.ty === ty;
    const t = setTimeout(() => { paintLand(k, tx, ty); if (!settled) { setDrawn({ k, tx, ty }); setSnapping(true); } }, settled || reduced() ? 0 : 950);
    return () => clearTimeout(t);
  }, [scene, k, tx, ty, dragging]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!snapping) return undefined; let r2; const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setSnapping(false)); }); return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); }; }, [snapping]);
  const cs = k / drawn.k;
  const canvasTransform = `translate(${tx - cs * drawn.tx}px, ${ty - cs * drawn.ty}px) scale(${cs})`;

  /* ctrl/⌘ + wheel (trackpad pinch) zooms; plain scrolling stays with the page */
  useEffect(() => {
    const el = svgRef.current; if (!el) return undefined;
    const onWheel = (e) => { if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault(); const r = el.getBoundingClientRect(); lastTouch.current = Date.now(); setSpot(null); zoomAt(Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active && !view && !openCluster && !listOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { touch(); setActive(null); setOpenCluster(null); setListOpen(false); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [active, view, openCluster, listOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  /* spotlight: on desktop, when nobody is interacting, move quietly from project to project */
  useEffect(() => {
    if (!desktop || !spotOn || !seen || !spotPool.length || reduced()) return undefined;
    const busy = () => Date.now() - lastTouch.current < IDLE_MS || active || openCluster || hover || hoverCluster || listOpen || company;
    const first = setTimeout(() => { if (!busy()) setSpot((s) => s || spotPool[0].slug); }, 1800);
    const id = setInterval(() => { if (busy()) return; setSpot((s) => { const i = spotPool.findIndex((p) => p.slug === s); return spotPool[(i + 1) % spotPool.length].slug; }); }, SPOT_MS);
    return () => { clearInterval(id); clearTimeout(first); };
  }, [desktop, spotOn, seen, spotPool, active, openCluster, hover, hoverCluster, listOpen, company]);

  /* mobile rail → map */
  const onRailScroll = () => {
    clearTimeout(railTimer.current);
    railTimer.current = setTimeout(() => {
      const rail = railRef.current; if (!rail) return;
      const mid = rail.scrollLeft + rail.clientWidth / 2;
      let best = null, bd = Infinity;
      for (const el of rail.children) { const c = el.offsetLeft + el.clientWidth / 2, d = Math.abs(c - mid); if (d < bd) { bd = d; best = el; } }
      const slug = best?.dataset.slug; if (!slug || slug === active) return;
      const p = listed.find((x) => x.slug === slug); if (p) select(p, { move: true });
    }, 120);
  };

  const focus = active || hover || (desktop && !openCluster && !hoverCluster ? spot : null); // drives the dock
  const current = focus || rowHover; // drives the highlighted pin
  const card = focus ? visible.find((p) => p.slug === focus) : null;
  const idx = card ? listed.findIndex((p) => p.slug === card.slug) : -1;
  const step = (dir) => { if (!listed.length) return; select(listed[(Math.max(idx, 0) + dir + listed.length) % listed.length]); };
  const placeGroup = openCluster ? groups.find((g) => g.key === openCluster) : null;
  const hoveredGroup = hoverCluster ? groups.find((g) => g.key === hoverCluster) : null;
  const screen = (x, y) => ({ left: `${((k * x + tx) / W) * 100}%`, top: `${((k * y + ty) / H) * 100}%` });
  const ready = !!scene && seen;
  const total = pins?.length || 0;
  const isSpot = !!card && !active && !hover;
  const lit = useMemo(() => new Set(card?.companies || []), [card]);
  const shownBases = (hoverCompany || company) ? (scene?.bases[hoverCompany || company] || []) : [];
  const toggleSpot = () => { setSpotOn((v) => !v); setSpot(null); lastTouch.current = 0; };

  const title = (
    <>
      <div className="sq flex items-center gap-4"><FloorLabel light>{t("Prosjekter")}</FloorLabel><span className="h-px w-16 bg-white/20" aria-hidden="true" /></div>
      <h2 className="sq mt-4 text-[length:var(--map-h2)] font-medium leading-[0.94] tracking-[-0.04em] text-white" style={{ "--d": "60ms" }}>{t("Vi er der")}<br />{t("industrien er.")}</h2>
    </>
  );
  const figures = (
    <dl className={`sq map-figures grid grid-cols-3 ${desktop ? "" : "mt-7 border-t border-white/15"}`} style={{ "--d": "180ms" }} data-testid="map-stats">
      {[[total, t("oppdrag"), 0], [companies, t("selskaper"), 1], [counties, t("fylker"), 2]].map(([v, l, i]) => (
        <div key={l} className={`${desktop ? "" : "pt-4"} ${i > 0 ? "border-l border-white/15 pl-6" : ""} ${desktop && i > 0 ? "ml-6" : ""}`}>
          <dd className="tabular text-[length:var(--map-fig)] font-medium leading-none text-white"><CountUp value={v} active={ready} delay={500 + i * 120} duration={1200} /></dd>
          <dt className="mt-2 t-label text-[11px] text-white/45">{l}</dt>
        </div>
      ))}
    </dl>
  );
  const intro = (<>{title}{figures}</>);
  const legend = (
    <div className={`map-legend ${desktop ? "sq flex flex-col items-start gap-2.5" : "flex flex-wrap gap-x-5 gap-y-2.5"}`} role="group" aria-label={t("Filtrer etter type")} style={{ "--d": "240ms" }}>
      {TYPES.map((t) => { const on = filter.has(t.id); return (
        <button key={t.id} type="button" data-testid={`map-filter-${t.id}`} aria-pressed={on} onClick={() => toggle(t.id)} className={`map-legend-item ${on ? "is-on" : ""}`}><Dot type={t.id} on={on} />{t.label}</button>); })}
    </div>
  );
  const listToggle = (
    <button type="button" data-testid="map-list-toggle" aria-expanded={listOpen} onClick={() => { touch(); setListOpen((v) => !v); setOpenCluster(null); }} className="sq link-arrow map-link mt-4 t-small" style={{ "--d": "300ms" }}>
      {listOpen ? t("Skjul listen") : listed.length === total ? t("Alle {n} oppdrag", { n: total }) : t("{n} oppdrag i utvalget", { n: listed.length })} <span className={`inline-flex transition-transform duration-300 ${listOpen ? "-rotate-90" : "rotate-90"}`}><ArrowRight size={14} /></span>
    </button>
  );

  let dock;
  if (listOpen) {
    dock = (
      <div key="list" className="map-panel fade-in flex min-h-0 flex-col" style={{ animationDuration: "220ms" }}>
        <div className="flex items-center justify-between pb-1"><span className="t-label text-white/55">{t("Alle {n} oppdrag", { n: listed.length })}</span><button type="button" data-testid="map-list-close" aria-label={t("Lukk listen")} onClick={() => setListOpen(false)} className="map-icon-btn -mr-2"><X size={15} /></button></div>
        <ol data-testid="map-list" className="map-list -mx-2 min-h-0 overflow-y-auto">
          {byCounty.map(([county, items]) => (
            <li key={county}>
              <p className="px-2 pb-1 pt-3 t-label text-[11px] text-white/40">{county}</p>
              <ul>
                {items.map((p) => (
                  <li key={p.slug}><button type="button" data-testid={`map-list-${p.slug}`} onMouseEnter={() => setHover(p.slug)} onMouseLeave={() => setHover(null)} onClick={() => select(p)} className={`map-row grid w-full grid-cols-[10px_1fr] items-baseline gap-3 px-2 py-2 text-left ${active === p.slug ? "is-current" : ""}`}>
                    <Dot type={p.type} /><span className="min-w-0"><span className="block truncate text-[14px] font-medium text-white">{p.name}</span><span className="block truncate text-[12.5px] text-white/50">{p.place} · {companiesOf(p)}</span></span>
                  </button></li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
    );
  } else if (placeGroup && !card) {
    dock = (
      <div key={placeGroup.key} data-testid="map-place-card" className="map-panel fade-in" style={{ animationDuration: "220ms" }}>
        <div className="flex items-center justify-between"><p className="t-label text-white/55">{regionLabel(placeGroup.items)}</p><button type="button" aria-label={t("Lukk")} data-testid="map-place-close" onClick={() => { touch(); setOpenCluster(null); }} className="map-icon-btn -mr-2"><X size={15} /></button></div>
        <ul className="-mx-2 mt-1">
          {placeGroup.items.map((p) => (
            <li key={p.slug}><button type="button" data-testid={`map-place-item-${p.slug}`} onClick={() => select(p, { move: false })} className="map-row grid w-full grid-cols-[10px_1fr] items-baseline gap-3 px-2 py-2.5 text-left">
              <Dot type={p.type} /><span className="min-w-0"><span className="block text-[15px] font-medium leading-snug text-white">{p.name}</span><span className="block t-small text-white/55">{companiesOf(p)} · {p.period}</span></span>
            </button></li>
          ))}
        </ul>
      </div>
    );
  } else if (company && !card && byCompany[company]) {
    const c = byCompany[company];
    dock = (
      <div key={`co-${c.slug}`} data-testid="map-company-card" className="map-panel fade-in flex min-h-0 flex-col" style={{ animationDuration: "260ms" }}>
        <div className="flex items-start justify-between gap-4">
          <img src={c.logo_white} alt={c.name} className="h-7 w-auto max-w-[220px]" decoding="async" />
          <button type="button" data-testid="map-company-close" aria-label={t("Vis alle selskaper")} title={t("Alle selskaper")} onClick={() => selectCompany(null)} className="map-icon-btn -mr-2 -mt-1"><X size={15} /></button>
        </div>
        <p className="mt-3 t-small text-white/55">{c.location} · <span className="tabular">{listed.length}</span> {t("oppdrag på kartet")}</p>
        {c.lead && <p className="mt-2 line-clamp-3 text-[14.5px] leading-relaxed text-white/75">{c.lead}</p>}
        <ul className="map-list -mx-2 mt-2 min-h-0 overflow-y-auto">
          {listed.map((p) => (
            <li key={p.slug}><button type="button" data-testid={`map-company-item-${p.slug}`} onMouseEnter={() => setRowHover(p.slug)} onMouseLeave={() => setRowHover(null)} onClick={() => { setRowHover(null); select(p, { move: false }); }} className="map-row grid w-full grid-cols-[10px_1fr] items-baseline gap-3 px-2 py-2 text-left">
              <Dot type={p.type} /><span className="min-w-0"><span className="block truncate text-[14px] font-medium text-white">{p.name}</span><span className="block truncate text-[12.5px] text-white/50">{p.place} · {p.period}</span></span>
            </button></li>
          ))}
        </ul>
        <Link to={`/selskaper/${c.slug}`} data-testid="map-company-link" className="link-arrow map-link mt-3 t-small">{t("Se selskapet")} <ArrowRight size={14} /></Link>
      </div>
    );
  } else if (view && spotPool.length && !card) {
    dock = (
      <div key="area" data-testid="map-area-list" className="map-panel fade-in flex min-h-0 flex-col" style={{ animationDuration: "220ms" }}>
        <p className="t-label text-white/55">{t("{n} oppdrag i området", { n: spotPool.length })}</p>
        <ul className="-mx-2 mt-1 min-h-0 overflow-y-auto map-list">
          {spotPool.map((p) => (
            <li key={p.slug}><button type="button" data-testid={`map-area-item-${p.slug}`} onMouseEnter={() => setRowHover(p.slug)} onMouseLeave={() => setRowHover(null)} onClick={() => { setRowHover(null); select(p, { move: false }); }} className="map-row grid w-full grid-cols-[10px_1fr] items-baseline gap-3 px-2 py-2 text-left">
              <Dot type={p.type} /><span className="min-w-0"><span className="block truncate text-[14px] font-medium text-white">{p.name}</span><span className="block truncate text-[12.5px] text-white/50">{p.place} · {companiesOf(p)}</span></span>
            </button></li>
          ))}
        </ul>
      </div>
    );
  } else if (card) {
    dock = <ProjectCard key={card.slug} p={card} idx={idx} total={listed.length} byCompany={byCompany} onPrev={() => step(-1)} onNext={() => step(1)} spot={isSpot} spotOn={spotOn} onToggleSpot={toggleSpot} />;
  } else {
    dock = (
      <div key="empty" data-testid="map-empty" className="map-panel fade-in" style={{ animationDuration: "220ms" }}>
        <p className="text-[15px] leading-relaxed text-white/60">{t("Velg et punkt på kartet.")}</p>
        <div className="-ml-2 mt-3 flex items-center"><button type="button" data-testid="map-prev" aria-label={t("Forrige oppdrag")} onClick={() => step(-1)} className="map-icon-btn"><ChevronLeft size={15} /></button><button type="button" data-testid="map-next" aria-label={t("Neste oppdrag")} onClick={() => step(1)} className="map-icon-btn"><ChevronRight size={15} /></button><button type="button" data-testid="map-spot-toggle" aria-pressed={spotOn} aria-label={spotOn ? "Pause visningen" : "Start visningen"} onClick={toggleSpot} className="map-icon-btn">{spotOn ? <Pause size={12} /> : <Play size={12} />}</button></div>
      </div>
    );
  }

  const brandBand = brands.length > 0 && (
    <div className="map-brands relative border-t border-white/10" data-testid="map-brands">
      <div className={`mx-auto flex max-w-[1920px] items-center gap-x-6 gap-y-3 px-[var(--gutter)] py-6 ${desktop ? "flex-wrap justify-between" : "map-brands-scroll overflow-x-auto"}`} role="group" aria-label={t("Velg selskap")}>
        <button type="button" data-testid="map-brands-all" aria-pressed={!company} onClick={() => selectCompany(null)} className={`map-brand-all shrink-0 t-label text-[11px] ${company ? "" : "is-on"}`}>{t("Alle selskaper")}</button>
        {brands.map((c) => { const on = company === c.slug; return (
          <button key={c.slug} type="button" data-testid={`map-brand-${c.slug}`} aria-pressed={on} title={c.name} aria-label={`${c.name}${on ? ` – ${t("vis alle")}` : ""}`}
            onMouseEnter={() => setHoverCompany(c.slug)} onMouseLeave={() => setHoverCompany(null)} onFocus={() => setHoverCompany(c.slug)} onBlur={() => setHoverCompany(null)}
            onClick={() => selectCompany(c.slug)} className={`map-brand shrink-0 ${on ? "is-on" : ""} ${!company && lit.has(c.slug) ? "is-lit" : ""}`}>
            <img src={c.logo_white} alt="" className="map-brand-img" decoding="async" />
          </button>); })}
      </div>
    </div>
  );

  const controls = (
    <div className={`map-controls absolute z-10 flex ${desktop ? "right-[var(--gutter)] top-[7%] flex-row gap-2" : "right-3 top-3 flex-col gap-2"}`}>
      <button type="button" data-testid="map-reset" aria-label={t("Hele landet")} title={t("Hele landet")} onClick={reset} className={`map-ctl transition-opacity duration-300 ${view ? "opacity-100" : "pointer-events-none opacity-0"}`}><Minimize2 size={14} /></button>
      <button type="button" data-testid="map-zoom-in" aria-label={t("Zoom inn")} title={t("Zoom inn")} disabled={k >= K_MAX - 0.01} onClick={() => { touch(); zoomAt(1.8, mapLeft + (W - mapLeft) / 2, H / 2); }} className="map-ctl"><Plus size={15} /></button>
      <button type="button" data-testid="map-zoom-out" aria-label={t("Zoom ut")} title={t("Zoom ut")} disabled={!view} onClick={() => { touch(); zoomAt(1 / 1.8, mapLeft + (W - mapLeft) / 2, H / 2); }} className="map-ctl"><Minus size={15} /></button>
    </div>
  );

  return (
    <section ref={ref} data-testid="floor-map" data-seq={ready ? "in" : "out"} data-ask-label={t("Spør om et prosjekt")} className="map-floor grain relative overflow-hidden bg-navy text-white">
      <div className="map-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      {!desktop && (
        <div className="relative">
          <div className="wrap pb-6 pt-14">{intro}</div>
          <div className="wrap pb-3">{legend}</div>
        </div>
      )}
      <div ref={stageRef} className={`map-stage relative mx-auto w-full max-w-[1920px] ${dragging ? "is-dragging" : ""}`} style={{ height: desktop ? "clamp(720px, 100svh, 1000px)" : "min(60svh, 560px)" }}>
        {err && <p className="absolute inset-0 grid place-items-center t-small text-white/60" data-testid="map-error">{t("Kartet kunne ikke lastes akkurat nå.")}</p>}
        {!scene && !err && <div className="absolute inset-0 animate-pulse bg-white/[0.02]" aria-busy="true" />}
        {scene && (
          <>
            <div className="map-plane absolute inset-0">
              <canvas ref={canvasRef} data-testid="map-land" className={`map-land-canvas pointer-events-none absolute inset-0 h-full w-full ${snapping ? "is-snapping" : ""}`} style={{ transform: canvasTransform }} aria-hidden="true" />
              <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W} height={H} className={`map-svg absolute inset-0 block ${k > 1 ? "cursor-grab" : ""} ${dragging ? "cursor-grabbing" : ""}`} role="img" aria-label={t("Kart over Norge med EQVA-selskapenes oppdrag")} style={{ touchAction: desktop ? "none" : "pan-y" }}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
                onClick={(e) => { if (dragged.current) return; if (e.target === e.currentTarget) { touch(); setActive(null); setOpenCluster(null); } }}
                onDoubleClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); touch(); zoomAt(2, e.clientX - r.left, e.clientY - r.top); }}>
                <defs>
                  {/* one soft shadow shared by every marker – a gradient fill, so no per-element filters */}
                  <radialGradient id="map-shadow-g"><stop offset="40%" stopColor="#000810" stopOpacity="0.42" /><stop offset="100%" stopColor="#000810" stopOpacity="0" /></radialGradient>
                </defs>
                <g className="map-zoom" style={{ transform: `translate(${tx}px, ${ty}px) scale(${k})` }}>
                  <g className="map-cities" aria-hidden="true">
                    {scene.cities.filter((c) => (!c.min || k >= c.min) && !(k > 2.4 && pinPlaces.has(c.n))).map((c) => (
                      <g key={c.n} transform={`translate(${c.x},${c.y})`} className="map-city-g"><g style={{ transform: `scale(${1 / k})` }} className="map-pin-inner"><circle r="1.5" className="map-city-dot" />{(() => { const flip = c.anchor === "end" && k * c.x + tx < 150; return <text x={flip ? 9 : c.dx} y={c.dy} textAnchor={flip ? "start" : c.anchor || "start"} className="map-city">{c.n}</text>; })()}</g></g>
                    ))}
                  </g>
                  {shownBases.map((b, i) => {
                    const crowded = groups.some((g) => Math.hypot(g.x - b.x, g.y - b.y) * k < 44);
                    return (
                      <g key={`base-${i}`} transform={`translate(${b.x},${b.y})`} className="map-base" aria-hidden="true">
                        <g className="map-pin-inner" style={{ transform: `scale(${1 / k})` }}>
                          <circle r="12" cy="2" className="map-shadow" />
                          <rect x="-6.5" y="-6.5" width="13" height="13" className="map-base-mark" />
                          <rect x="-2.5" y="-2.5" width="5" height="5" className="map-base-core" />
                          {!crowded && <text x="15" dy="4" className="map-pin-label">{b.place} · {t("hovedkontor")}</text>}
                        </g>
                      </g>
                    );
                  })}
                  {/* beads first, discs on top (largest last) so nothing ever sits under a neighbour */}
                  {[...groups].sort((a, b) => a.items.length - b.items.length).map((g, gi) => {
                    const p = g.items[0];
                    const delay = view ? `${(gi % 12) * 35}ms` : `${Math.round(400 + p.t * 1200)}ms`;
                    if (g.items.length > 1) {
                      const n = g.items.length, R = clusterR(n), arcs = ringArcs(g.items, R + 3.5);
                      const isCur = g.items.some((i) => i.slug === active) || openCluster === g.key || (spot && !active && !hover && g.items.some((i) => i.slug === spot));
                      return (
                        <g key={g.key} transform={`translate(${g.x},${g.y})`} className={`map-pin ${isCur ? "is-current" : ""} ${hoverCompany && !company && !g.items.some((i) => i.companies?.includes(hoverCompany)) ? "is-dim" : ""}`} style={{ "--d": delay }}>
                          <g className="map-pin-inner" style={{ transform: `scale(${1 / k})` }}>
                            <g className="map-pop"><g role="button" tabIndex={0} data-testid={`map-cluster-${n}`} aria-label={t("{n} oppdrag: {places}. Zoom inn.", { n, places: regionLabel(g.items) })} className="map-cluster cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); if (!dragged.current) zoomTo(g); }} onMouseEnter={() => setHoverCluster(g.key)} onMouseLeave={() => setHoverCluster(null)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); zoomTo(g); } }}>
                              <circle r={R + 14} className="map-cluster-halo" />
                              <circle r={R * 1.45} cy={R * 0.22} className="map-shadow" />
                              <circle r={R} className="map-cluster-core" />
                              <g className="map-cluster-ring">
                                {arcs.map((a) => (a.full ? <circle key={a.id} r={R + 3.5} stroke={a.color} /> : <path key={a.id} d={a.d} stroke={a.color} />))}
                              </g>
                              <text textAnchor="middle" dy={R > 20 ? 5.5 : 5} className={`map-cluster-count ${R > 20 ? "is-big" : ""}`}>{n}</text>
                            </g></g>
                          </g>
                        </g>
                      );
                    }
                    const isCur = current === p.slug;
                    return (
                      <g key={p.slug} transform={`translate(${p.x},${p.y})`} className={`map-pin ${isCur ? "is-current" : ""} ${((active || hover) && !isCur) || (hoverCompany && !company && !p.companies?.includes(hoverCompany)) ? "is-dim" : ""}`} style={{ "--d": delay, "--c": TYPE[p.type]?.color }}>
                        <g className="map-pin-inner" style={{ transform: `scale(${1 / k})` }}>
                          <g className="map-pop"><g role="button" tabIndex={0} data-testid={`map-pin-${p.slug}`} aria-label={`${p.name}, ${p.place}`} aria-pressed={active === p.slug} className="map-bead cursor-pointer"
                            onMouseEnter={() => setHover(p.slug)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(p.slug)} onBlur={() => setHover(null)}
                            onClick={(e) => { e.stopPropagation(); pick(p); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(p); } }}>
                            <circle r="22" className="map-pin-hit" />
                            <circle r="16" className="map-pin-halo" />
                            <circle r={BEAD_R * 1.5} cy="2" className="map-shadow" />
                            <circle r={BEAD_R} className="map-pin-ring" />
                            <circle r="5.4" className="map-pin-dot" />
                            {(k > 2.4 || isCur) && <text x={BEAD_R + 9} dy="4.5" className="map-pin-label">{p.place}</text>}
                          </g></g>
                        </g>
                      </g>
                    );
                  })}
                </g>
              </svg>
            </div>

            {desktop && (
              <>
                {/* left: the statement, the numbers, the legend */}
                <div className="map-intro absolute bottom-[7%] left-[var(--gutter)] top-[7%] flex w-[clamp(300px,24vw,460px)] flex-col">
                  {title}
                  <div className="mt-8">{figures}</div>
                  <div className="mt-auto pt-8">{legend}{listToggle}</div>
                </div>
                {/* right: the card + zoom */}
                <div className="absolute bottom-[7%] right-[var(--gutter)] flex w-[clamp(300px,24vw,460px)] flex-col items-end gap-3">
                  <div className="map-dock map-glass sq flex max-h-[70vh] w-full flex-col" style={{ "--d": "420ms" }} data-testid="map-dock">{dock}</div>
                </div>
              </>
            )}

            {controls}

            {hover && desktop && (() => { const p = visible.find((x) => x.slug === hover); const g = p && groupOf[p.slug]; return p && g && g.items.length === 1 ? <div className="map-tip pointer-events-none absolute z-10 whitespace-nowrap" style={screen(p.x, p.y)} data-testid="map-pin-tip">{p.name}</div> : null; })()}
            {hoveredGroup && <div className="map-tip pointer-events-none absolute z-10 whitespace-nowrap tabular" style={screen(hoveredGroup.x, hoveredGroup.y)} data-testid="map-cluster-tip">{t("{n} oppdrag", { n: hoveredGroup.items.length })} · {regionLabel(hoveredGroup.items)}</div>}
          </>
        )}
      </div>

      {brandBand}
      {!desktop && scene && (
        <div className="relative pt-5">
          <div ref={railRef} onScroll={onRailScroll} data-testid="map-rail" className="map-rail flex snap-x snap-mandatory gap-4 overflow-x-auto px-[var(--gutter)] pb-2">
            {listed.map((p, i) => (
              <div key={p.slug} data-slug={p.slug} className={`map-rail-card snap-center shrink-0 ${active === p.slug ? "is-current" : ""}`}>
                <ProjectCard p={p} idx={i} total={listed.length} byCompany={byCompany} compact testid={`map-rail-${p.slug}`} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="wrap pb-10 pt-8"><p className="t-small text-white/30">{t("Utvalg fra offentlige kilder. Posisjonen viser stedet, ikke anlegget.")}</p></div>
    </section>
  );
};
